import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import Selector from "components/select/selector";
import { useTranslation } from "utils/locales/utilityFunctions";
import { beautifyNumber } from "utils/numberModification";
import MeasurementSectionFields from "./measurementSectionFields";
import { FirestoreInventoryItemProps } from "types/auth";

interface RawWoodInputMeasurementProps {
  variant: FirestoreInventoryItemProps["inventoryType"];
  disableInputs: boolean;
  buyerApprovedDisable: boolean;
  numberOfSections: number | "";
  numberOfSectionsBack: number | "";
  measureBothSides: boolean;
  stackLengthFront: string;
  stackLengthBack: string;
  frontHeights: string[];
  backHeights: string[];
  setNumberOfSections: (value: number | "") => void;
  setNumberOfSectionsBack: (value: number | "") => void;
  setMeasureBothSides: (value: boolean) => void;
  setStackLengthFront: (value: string) => void;
  setStackLengthBack: (value: string) => void;
  setFrontHeights: (heights: string[]) => void;
  setBackHeights: (heights: string[]) => void;
}

const RawWoodInputMeasurement = ({
  variant,
  disableInputs,
  buyerApprovedDisable,
  numberOfSections,
  numberOfSectionsBack,
  measureBothSides,
  stackLengthFront,
  stackLengthBack,
  frontHeights,
  backHeights,
  setNumberOfSections,
  setNumberOfSectionsBack,
  setMeasureBothSides,
  setStackLengthFront,
  setStackLengthBack,
  setFrontHeights,
  setBackHeights,
}: RawWoodInputMeasurementProps) => {
  const { translate } = useTranslation();

  // --- States --- //
  const sectionNumbers = [...Array(101).keys()].slice(1); // [1..1000]
  const SECTION_OPTIONS = sectionNumbers.map((el) => ({
    id: el.toString(),
    status: "enabled",
    label: el.toString(),
  }));

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5">
          {translate("input-dialog-raw-wood-tab-measurement-measurement")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={4}>
        <Selector
          title={translate("input-dialog-raw-wood-tab-measurement-measurement-stack-length-front")}
          value={stackLengthFront}
          firstItemCustomValue
          options={Array.from({ length: 1000 }, (_, i) => ({
            id: (i + 1).toString(),
            label: `${i + 1} m`,
            status: "enabled",
          }))}
          label={true}
          fullWidth={true}
          error={false}
          disabled={disableInputs || buyerApprovedDisable}
          changeHandler={(e) => {
            const val = e.target.value;
            setStackLengthFront(val);
          }}
        />
      </Grid>

      <Grid item xs={6} sm={4}>
        <Stack direction={"row"} spacing={1}>
          <Selector
            title={`${translate("input-dialog-raw-wood-tab-measurement-measurement-sections")} – ${translate("input-dialog-raw-wood-tab-measurement-measurement-front")}`}
            value={numberOfSections.toString()}
            options={SECTION_OPTIONS}
            label={true}
            fullWidth={true}
            error={false}
            disabled={disableInputs || buyerApprovedDisable}
            changeHandler={(e) => {
              const val = e.target.value;
              setNumberOfSections(val === "" ? "" : Number(val));
            }}
          />

          <Divider orientation="vertical" flexItem />
        </Stack>

        {stackLengthFront !== "" && numberOfSections !== "" && numberOfSections > 0 && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("input-dialog-raw-wood-tab-measurement-measurement-section-length")}{" "}
            {beautifyNumber(Number(stackLengthFront.replace(",", ".")) / numberOfSections, 1)} m
          </Typography>
        )}
      </Grid>

      <Grid item xs={6} sm={4} alignItems={"center"} sx={{ mt: 0.4 }}>
        <Tooltip
          title={translate(
            "input-dialog-raw-wood-tab-measurement-measurement-measure-both-sides-tooltip"
          )}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={measureBothSides}
                disabled={disableInputs || buyerApprovedDisable}
                onChange={() => setMeasureBothSides(!measureBothSides)}
              />
            }
            label={translate(
              "input-dialog-raw-wood-tab-measurement-measurement-measure-both-sides"
            )}
            sx={{ ml: -1 }}
          />
        </Tooltip>
      </Grid>

      {measureBothSides && (
        <>
          <Grid item xs={12} sm={4}>
            <Selector
              title={translate("input-dialog-raw-wood-tab-measurement-measurement-stack-length-back")}
              value={stackLengthBack}
              firstItemCustomValue
              options={Array.from({ length: 1000 }, (_, i) => ({
                id: (i + 1).toString(),
                label: `${i + 1} m`,
                status: "enabled",
              }))}
              label={true}
              fullWidth={true}
              error={false}
              disabled={disableInputs || buyerApprovedDisable}
              changeHandler={(e) => setStackLengthBack(e.target.value)}
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Stack direction="row" spacing={1}>
              <Selector
                title={`${translate("input-dialog-raw-wood-tab-measurement-measurement-sections")} – ${translate("input-dialog-raw-wood-tab-measurement-measurement-back")}`}
                value={numberOfSectionsBack.toString()}
                options={SECTION_OPTIONS}
                label fullWidth error={false}
                disabled={disableInputs || buyerApprovedDisable}
                changeHandler={e => setNumberOfSectionsBack(e.target.value === "" ? "" : Number(e.target.value))}
              />
              <Divider orientation="vertical" flexItem />
            </Stack>
            {stackLengthBack !== "" && numberOfSectionsBack !== "" && numberOfSectionsBack > 0 && (
              <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
                {translate("input-dialog-raw-wood-tab-measurement-measurement-section-length")}{" "}
                {beautifyNumber(Number(stackLengthBack.replace(",", ".")) / numberOfSectionsBack, 1)} m
              </Typography>
            )}
          </Grid>
        </>
      )}

      {((stackLengthFront !== "" && Number(numberOfSections) > 0) || (measureBothSides && stackLengthBack !== "" && Number(numberOfSectionsBack) > 0)) && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <MeasurementSectionFields
            disableInputs={disableInputs || buyerApprovedDisable}
            sectionCount={stackLengthFront ? Number(numberOfSections) : 0}
            backSectionCount={stackLengthBack ? Number(numberOfSectionsBack) : 0}
            measureBothSides={measureBothSides}
            frontTitle={translate("input-dialog-raw-wood-tab-measurement-measurement-front")}
            frontHeights={frontHeights}
            backTitle={translate("input-dialog-raw-wood-tab-measurement-measurement-back")}
            backHeights={backHeights}
            setFrontHeights={setFrontHeights}
            setBackHeights={setBackHeights}
          />
        </>
      )}
    </>
  );
};

export default RawWoodInputMeasurement;
